import { AfterViewInit, Component, ElementRef, Inject, ViewChild, DOCUMENT } from '@angular/core';

interface IContentsEtry {
  children: [];
  id: string;
  level: number;
  textContent: string;
}

@Component({
  selector: 'app-table-of-contents',
  templateUrl: './table-of-contents.component.html',
  standalone: false
})
export class TableOfContentsComponent implements AfterViewInit {
  @ViewChild('content') content: ElementRef<HTMLElement>;
  @ViewChild('tableOfContents') tableOfContents: ElementRef<HTMLUListElement>;

  isEmpty = true;
  isExpanded = false;

  private prefix = 'toc';
  private separator = '-';
  private tags = ['H1', 'H2', 'H3', 'H4', 'H5', 'H6'];
  private blacklistedChars = new RegExp(`[^a-z\\d${this.separator}]`, 'gi');
  private whitespaceChars = /\s+/g;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject('APP') public APP: string
  ) {}

  ngAfterViewInit() {
    this.renderTableOfContents();
  }

  private get pathname() {
    return location.pathname.replace(/^\/dashboard/i, '');
  }

  private renderTableOfContents() {
    const outline = [];

    this.content.nativeElement.querySelectorAll(this.tags.join()).forEach((heading: HTMLHeadingElement, index) => {
      heading.id = this.generateUniqueId(heading.textContent as string, index);

      const entry: IContentsEtry = {
        children: [],
        id: heading.id,
        level: this.tags.indexOf(heading.tagName),
        textContent: heading.textContent as string
      };

      this.addEntryToOutline(outline, entry);
    });

    if (outline.length > 0) {
      this.populateList(this.tableOfContents.nativeElement, outline);
      this.isEmpty = false;
    }
  }

  private generateUniqueId(value: string, i: number) {
    const id = value.replace(this.whitespaceChars, this.separator).replace(this.blacklistedChars, '');

    // Unique prefix to be able to process links to these ids based on a heading position in a document,
    // it helps to link to the same content in different languages (see scroll-to-id.service).
    return `${this.prefix}${i}.${id}`;
  }

  private addEntryToOutline(outline: IContentsEtry[], entry: IContentsEtry) {
    const tail = outline[outline.length - 1];

    if (tail && tail.level < entry.level) {
      this.addEntryToOutline(tail.children, entry);
    } else {
      outline.push(entry);
    }

    return outline;
  }

  private populateList(list: HTMLUListElement, outline: IContentsEtry[]) {
    outline.forEach((entry: IContentsEtry) => {
      const anchor = this.createAnchorElement(entry);
      const li = this.createListItemElement();
      li.append(anchor);

      if (entry.children.length) {
        const ul = this.createListElement();
        this.populateList(ul, entry.children);
        li.append(ul);
      }

      list.append(li);
    });
  }

  private createListElement(): HTMLUListElement {
    const ul = document.createElement('ul');
    ul.classList.add('table-of-contents_list');

    return ul;
  }

  private createListItemElement(): HTMLLIElement {
    const li = document.createElement('li');
    li.classList.add('table-of-contents_item');

    return li;
  }

  private createAnchorElement(entry: IContentsEtry): HTMLAnchorElement {
    const a = document.createElement('a');

    a.classList.add('table-of-contents_link');
    a.textContent = entry.textContent;
    a.setAttribute('href', `${this.pathname}?scrollToId=${entry.id}`);
    a.addEventListener('click', event => {
      event.preventDefault();

      const scrollingContainer = document.getElementById('scrolling-container');
      if (scrollingContainer) {
        scrollingContainer.scrollTop = (document.getElementById(entry.id) as HTMLElement).offsetTop;
      }
    });

    return a;
  }
}
